export const normalizeDatastreamNameToken = (value) => String(value ?? '').trim().toLowerCase();

export const stripDatastreamCopySuffix = (value) =>
    String(value ?? '')
        .trim()
        .replace(/(?:-copy)(?:-(\d+))?$/i, '')
        .trim();

export const buildUniqueDatastreamCopyName = (sourceName, datastreams = []) => {
    const fallbackBaseName = 'datastream';
    const rawBaseName = stripDatastreamCopySuffix(sourceName);
    const baseName = rawBaseName || fallbackBaseName;

    const usedNames = new Set(
        (Array.isArray(datastreams) ? datastreams : [])
            .filter(Boolean)
            .map((datastream) => normalizeDatastreamNameToken(datastream?.name))
            .filter(Boolean)
    );

    const firstCandidate = `${baseName}-copy`;
    if (!usedNames.has(normalizeDatastreamNameToken(firstCandidate))) {
        return firstCandidate;
    }

    let duplicateIndex = 2;
    while (usedNames.has(normalizeDatastreamNameToken(`${baseName}-copy-${duplicateIndex}`))) {
        duplicateIndex += 1;
    }

    return `${baseName}-copy-${duplicateIndex}`;
};

export const getNextAvailableVirtualPin = (usedPins) => {
    for (let i = 0; i < 500; i++) {
        const pin = `V${i}`;
        if (!usedPins.has(pin)) return pin;
    }
    return '';
};

export const resolveDuplicateDatastreamSource = (payloadOrId, datastreams = []) => {
    const existing = (datastreams || []).filter(Boolean);
    const sourceId =
        typeof payloadOrId === 'number'
            ? payloadOrId
            : typeof payloadOrId === 'string'
                ? parseInt(payloadOrId)
                : payloadOrId?.datastream?.id;

    return (
        (payloadOrId && payloadOrId.datastream) ||
        existing.find((datastream) => Number(datastream.id) === Number(sourceId)) ||
        null
    );
};

export const buildDuplicateDatastreamDraft = ({
    source,
    existing,
    payloadOrId,
    templateEvaluationData,
    getNextDatastreamId,
}) => {
    const usedPins = new Set(existing.map((datastream) => datastream.pin).filter(Boolean));
    const newId = getNextDatastreamId(existing);

    let newPin = source.pin;
    if (!newPin || usedPins.has(newPin)) {
        newPin = getNextAvailableVirtualPin(usedPins);
    }

    const clonedDatastream = {
        ...source,
        id: newId,
        pin: newPin,
        name: buildUniqueDatastreamCopyName(source?.name, existing),
        reportPriority: Math.max(
            0,
            ...existing.map((datastream) => Number(datastream?.reportPriority) || 0)
        ) + 1,
    };

    const incomingEvals = payloadOrId?.evaluations;
    const prevRanges = templateEvaluationData?.datastream_ranges || [];

    const sourceRanges = Array.isArray(incomingEvals)
        ? incomingEvals
        : prevRanges.filter((range) => Number(range.datastream_id) === Number(source.id));

    const clonedRanges = (sourceRanges || []).map((range) => ({
        ...range,
        datastream_evaluation_id: null,
        datastream_id: newId,
    }));

    return { newId, clonedDatastream, clonedRanges };
};

export const removeDatastreamById = (datastreams, id) =>
    datastreams?.filter((datastream) => datastream.id !== id);

export const removeEvaluationRangesByDatastreamId = (ranges, id) =>
    ranges.filter((range) => range.datastream_id !== id);

export const removeDatastreamsByIds = (datastreams, ids) =>
    datastreams?.filter((datastream) => !ids.includes(datastream.id));

export const removeEvaluationRangesByDatastreamIds = (ranges, ids) =>
    ranges.filter((range) => !ids.includes(range.datastream_id));

export const buildDatastreamEditState = (id) => ({
    edit: true,
    dataId: parseInt(id),
});

export const buildEventEditState = (id) => ({
    editEvents: true,
    eventId: parseInt(id),
});

export const buildDatastreamBulkDeleteDetails = (details, ids) => ({
    ...details,
    datastreams: removeDatastreamsByIds(details.datastreams, ids),
});

export const buildDatastreamBulkDeleteEvaluationData = (templateEvaluationData, ids) => ({
    ...templateEvaluationData,
    datastream_ranges: removeEvaluationRangesByDatastreamIds(
        templateEvaluationData.datastream_ranges,
        ids
    ),
});
